import React from 'react';

import type { HandlerRecord } from '@vlabs/api/artemy/model';
import { POLICIES } from '@vlabs/api-bindings/src/luna-client/handlers/constants';
import { Control } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import {
  ViewCard, ViewRow, ViewRowCheckbox, ViewRowTitle,
} from '../components';
import { useHandlerViewPageTranslation } from '../i18n';
import {
  extractFilters, hasFilters, isEnabled, isLunaWsCallback,
} from '../viewModel';
import { FilterRows } from './FilterRows';
import { optionLabel } from './options';

interface StorageSectionProps {
  handler: HandlerRecord;
}

interface StorageCardProps {
  value: string;
  enabled: boolean;
  title: string;
  defaultOpen?: boolean;
  filters?: unknown;
  ttl?: number | null;
  extraRows?: React.ReactNode;
}

function StorageCard({
  value,
  enabled,
  title,
  defaultOpen = true,
  filters = undefined,
  ttl = undefined,
  extraRows = undefined,
}: StorageCardProps): React.JSX.Element {
  const { t: tView } = useHandlerViewPageTranslation();

  const ttlLabel = ttl !== undefined && ttl !== null
    ? optionLabel(POLICIES.TTL, ttl) ?? String(ttl)
    : undefined;

  const rows: React.ReactNode[] = [];
  if (extraRows) rows.push(extraRows);
  if (hasFilters(extractFilters(filters))) {
    rows.push(<FilterRows
      filters={filters}
      key="filters"
    />);
  }
  if (ttlLabel) {
    rows.push(
      <ViewRow
        key="ttl"
        label={tView('storage.ttl')}
      >
        {ttlLabel}
      </ViewRow>,
    );
  }

  return (
    <ViewCard
      defaultOpen={defaultOpen}
      header={(
        <ViewRowTitle label={title}>
          <Control.Checkbox
            checked={enabled}
            disabled
          />
        </ViewRowTitle>
      )}
      value={value}
    >
      {rows.length > 0 ? rows : undefined}
    </ViewCard>
  );
}

function StorageSection({ handler }: StorageSectionProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { t: tView } = useHandlerViewPageTranslation();
  const storagePolicy = handler.policies.storage_policy;
  if (!storagePolicy) return null;

  const cards: React.ReactNode[] = [];

  if (storagePolicy.face_sample_policy && storagePolicy.face_sample_policy.store_sample !== 0) {
    cards.push((
      <StorageCard
        enabled={isEnabled(storagePolicy.face_sample_policy.store_sample)}
        filters={storagePolicy.face_sample_policy.filters}
        key="face-sample"
        title={tView('storage.face_sample')}
        ttl={storagePolicy.face_sample_policy.ttl}
        value="face-sample"
      />
    ));
  }

  if (storagePolicy.body_sample_policy && storagePolicy.body_sample_policy.store_sample !== 0) {
    cards.push((
      <StorageCard
        enabled={isEnabled(storagePolicy.body_sample_policy.store_sample)}
        filters={storagePolicy.body_sample_policy.filters}
        key="body-sample"
        title={tView('storage.body_sample')}
        ttl={storagePolicy.body_sample_policy.ttl}
        value="body-sample"
      />
    ));
  }

  const imageOriginPolicy = storagePolicy.image_origin_policy;
  if (imageOriginPolicy && imageOriginPolicy.store_image !== 0) {
    cards.push((
      <StorageCard
        enabled={isEnabled(imageOriginPolicy.store_image)}
        extraRows={(
          <ViewRowCheckbox
            checked={isEnabled(imageOriginPolicy.use_external_references)}
            label={t('handlers:policies.imageOrigin.useExternalReference')}
          />
        )}
        filters={imageOriginPolicy.filters}
        key="image-origin"
        title={tView('storage.image_origin')}
        ttl={imageOriginPolicy.ttl}
        value="image-origin"
      />
    ));
  }

  const eventPolicy = storagePolicy.event_policy;
  if (eventPolicy && eventPolicy.store_event !== 0) {
    cards.push((
      <StorageCard
        enabled={isEnabled(eventPolicy.store_event)}
        filters={eventPolicy.filters}
        key="event"
        title={tView('storage.event')}
        value="event"
      />
    ));
  }

  const operatorCallback = (storagePolicy.callbacks ?? []).find(isLunaWsCallback);
  if (operatorCallback) {
    cards.push((
      <StorageCard
        defaultOpen={operatorCallback.enable !== 0}
        enabled={operatorCallback.enable !== 0}
        filters={operatorCallback.filters}
        key="operator-panel"
        title={tView('storage.operator_panel')}
        value="operator-panel"
      />
    ));
  }

  if (cards.length === 0) return null;

  return <React.Fragment>{cards}</React.Fragment>;
}

export { StorageSection };
